import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Eraser, PenLine } from "lucide-react";

/**
 * SignaturePad — canvas drawing → base64 PNG data URL.
 * Props:
 *  - open (bool)
 *  - onOpenChange (fn)
 *  - onSave (fn(dataUrl: string))
 *  - title (string)
 *  - initialSignature (optional base64 data URL, shown as preview)
 */
export default function SignaturePad({ open, onOpenChange, onSave, title = "Signature", initialSignature }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // Fill white background so PDF isn't transparent (ReportLab Image handles it fine either way)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setHasDrawn(false);
  }, [open]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const t = e.touches?.[0];
    const cx = t ? t.clientX : e.clientX;
    const cy = t ? t.clientY : e.clientY;
    return { x: (cx - rect.left) * scaleX, y: (cy - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    setHasDrawn(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const save = () => {
    if (!hasDrawn) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave?.(dataUrl);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-4 w-4" /> {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {initialSignature && typeof initialSignature === "string" && initialSignature.startsWith("data:") && !hasDrawn && (
            <div className="text-xs text-slate-500">
              Signature actuelle :
              <img src={initialSignature} alt="signature actuelle" className="mt-1 h-16 border border-stone-200 rounded bg-white p-1" />
            </div>
          )}
          <div className="text-xs text-slate-500">
            Dessinez votre signature dans le cadre ci-dessous (souris ou doigt sur tactile).
          </div>
          <canvas
            ref={canvasRef}
            width={640}
            height={220}
            data-testid="signature-canvas"
            className="w-full h-48 border-2 border-dashed border-stone-300 rounded-lg bg-white touch-none cursor-crosshair"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <button type="button" onClick={clear} className="inline-flex items-center gap-1 hover:text-rose-600" data-testid="signature-clear">
              <Eraser className="h-3.5 w-3.5" /> Effacer
            </button>
            <span>{hasDrawn ? "Prêt à enregistrer" : "En attente…"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            data-testid="signature-save"
            onClick={save}
            disabled={!hasDrawn}
            className="bg-[hsl(var(--primary))]"
          >
            Enregistrer la signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
